import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AuthDisclaimer } from "@/components/AuthDisclaimer";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, Loader2, MapPin, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Connexion réussie");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error("Identifiants invalides. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0B1120]">
      <div className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(234,179,8,0.12), transparent 40%)" }} />

      <div className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-2xl shadow-2xl p-8 backdrop-blur-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 flex">
          <div className="flex-1 bg-emerald-600" />
          <div className="flex-1 bg-yellow-500" />
          <div className="flex-1 bg-red-600" />
        </div>

        <div className="flex items-center justify-center gap-3 mb-6 pb-5 border-b border-slate-800">
          <div className="w-12 h-12 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="text-left">
            <h1 className="font-display text-xl font-bold text-slate-100 leading-tight">Sama Jaamu</h1>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Centre de Supervision SOS</p>
          </div>
        </div>

        <div className="mb-6 text-center">
          <span className="bg-emerald-950 text-emerald-400 border border-emerald-500/30 text-xs px-3 py-1 rounded-full font-semibold">
            Accès réservé aux superviseurs
          </span>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-slate-400">Identifiant ou e-mail</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="email"
                data-testid="login-email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-slate-950 border-slate-700 text-slate-100 h-11"
                placeholder="admin ou manager@samajaamu.sn"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-slate-400">Mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="password"
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 bg-slate-950 border-slate-700 text-slate-100 h-11"
                placeholder="••••••••••"
                required
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter au centre"}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => navigate("/mot-de-passe-oublie")}
            className="text-xs font-mono text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
          >
            Mot de passe oublié ?
          </button>
        </div>

        <AuthDisclaimer />

        <p className="mt-6 text-center text-xs text-slate-500">
          Sama Jaamu — Dispositif d'alerte et de sécurité
        </p>
      </div>
    </div>
  );
};

export default Login;
